import { Box } from '@breathelife/mui';
import { Fragment, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Permission, StoredFileWithExistsProperty, supportedApplicationFileMimeTypes } from '@breathelife/types';

import { UploadButton } from '../../../../../../Components/Button/UploadButton';
import { RestrictedToUserPermissions } from '../../../../../Restricted/RestrictedToUserPermissions';
import { useCarrierContext } from '../../../../../../Hooks';
import { Application } from '../../../../../../Models/Application';
import { NoFiles } from '../NoFiles';
import { OtherFileItem } from './OtherFileItem';

type FileProps = {
  application: Application;
  files: StoredFileWithExistsProperty[];
  isUploading: boolean;
  isUploadDisabled: boolean;
  onFileUpload: (file: File) => void;
  onFileDelete: (fileId: string, isRequired: boolean) => void;
};

export function OtherFiles(props: FileProps): ReactElement | null {
  const { t } = useTranslation();
  const { application, files, isUploading, isUploadDisabled, onFileUpload, onFileDelete } = props;
  const {
    carrierInfo: { validFileUploadMimeTypes },
  } = useCarrierContext();

  const mimeTypes =
    validFileUploadMimeTypes && validFileUploadMimeTypes.length > 0
      ? validFileUploadMimeTypes
      : supportedApplicationFileMimeTypes;

  const uploadFileButton = (
    <UploadButton
      onChange={onFileUpload}
      mimeTypes={mimeTypes}
      disabled={isUploadDisabled}
      isLoading={isUploading}
      loaderProps={{
        color: 'secondary',
      }}
      variant='contained'
      color='primary'
      component='label'
    >
      {t('assistedApplication.documentsDrawer.uploadFile')}
    </UploadButton>
  );

  if (!files.length) {
    return (
      <Box>
        <NoFiles message={t('assistedApplication.documentsDrawer.noFilesUploaded')} />
        <RestrictedToUserPermissions requiredPermissions={[Permission.ApplicationFileCreate]}>
          <Box display='flex' justifyContent='center' alignItems='center'>
            {uploadFileButton}
          </Box>
        </RestrictedToUserPermissions>
      </Box>
    );
  }

  return (
    <Fragment>
      {files.map((storedFile) => (
        <Box my={2}>
          <OtherFileItem
            application={application}
            storedFile={storedFile}
            key={storedFile.id}
            onFileDelete={onFileDelete}
          />
        </Box>
      ))}
      <RestrictedToUserPermissions requiredPermissions={[Permission.ApplicationFileCreate]}>
        <Box py={3} display='flex' justifyContent='center' alignItems='center'>
          {uploadFileButton}
        </Box>
      </RestrictedToUserPermissions>
    </Fragment>
  );
}
